import { computed, Injectable, signal, WritableSignal } from "@angular/core";
import { PurchaseListItem } from "../models/purchase-list-item";
import { HasUnsavedChanges } from "../services/interfaces/has-unsaved-changes.interface";
import { GlobalResetService } from "../services/global-reset.service";
import { ProductListItem } from "../models/product-list/productListItem";
import { ProductApiService } from "../services/productApi/product-api.service";

export type CustomActionButton = {
  label: string;
  action: () => void;
};

@Injectable({
  providedIn: 'root',
})
export class PurchaseListStore implements HasUnsavedChanges {
  private readonly DEFAULT_ADD_ANOTHER_PRODUCT_LABEL = 'purchase-list.add-another-product';

  isSentToComPlus: boolean;
  opportunityNumber: number | null;

  /**
   * Contient à plat : produits simples, parents de bundle (isBundleParent=true, masqués dans l'UI)
   * et enfants de bundle (bundleParentEan set, quantité indépendante).
   */
  products = signal<PurchaseListItem[]>([]);
  totalItemCount = signal(0);
  totalListPrice = signal(0);
  customActionButton = signal<CustomActionButton | null>(null);
  addAnotherProductLabel = signal<string>(this.DEFAULT_ADD_ANOTHER_PRODUCT_LABEL);

  /**
   * Pourcentage de remise choisi
   */
  discountPercentage: WritableSignal<number> = signal(null);

  /**
   * Montant de la remise calculé à partir du pourcentage et du prix total
   */
  discountAmount = computed(() => this.totalListPrice() * (this.discountPercentage() / 100));

  /**
   * Montant total de la liste d'achat avec remise appliquée
   */
  totalListPriceWithDiscount = computed(() => this.totalListPrice() - this.discountAmount());

  constructor(
    private readonly globalResetService: GlobalResetService,
    private readonly productApi: ProductApiService
  ) {
    this.globalResetService.register(() => this.reset());
  }

  hasUnsavedChanges(): boolean {
    return this.products().length > 0;
  }

  /**
   * Items affichés dans la liste d'achat : produits simples + enfants de bundle.
   * Les parents de bundle (isBundleParent=true) sont exclus de l'affichage.
   */
  displayItems = computed<PurchaseListItem[]>(() => this.products().filter(item => !item.isBundleParent));

  public async addToPurchaseList(input: PurchaseListItem, replaceAmount: boolean = false): Promise<void> {
    if (input.product.bundleBillOfMaterials?.length && !input.bundleParentEan && !input.isBundleParent) {
      // Nouveau bundle : on stocke le parent (isBundleParent=true) et chaque enfant individuellement
      const parentItem: PurchaseListItem = { product: input.product, amount: input.amount, isBundleParent: true };
      this.addSingleProduct(parentItem, replaceAmount);

      for (const child of input.product.bundleBillOfMaterials) {
        const response = await this.productApi.sendSearchRequest({ searchTerm: child.ean, sortBy: null, page: 1 });
        if (response.data?.length > 0) {
          const childProduct = new ProductListItem(response.data[0]);
          this.addSingleProduct(
            { product: childProduct, amount: input.amount, bundleParentEan: input.product.ean },
            replaceAmount
          );
        }
      }
    } else {
      // Produit simple, ou mise à jour de quantité d'un enfant (bundleParentEan set) → traitement direct
      this.addSingleProduct(input, replaceAmount);
    }
    this.onPurchaseListUpdated();
  }

  private addSingleProduct(input: PurchaseListItem, replaceAmount: boolean = false): void {
    this.products.update(items => {
      const existingIndex = items.findIndex(item => item.product.ean === input.product.ean);
      if (existingIndex !== -1) {
        const updatedItems = [...items];
        if (replaceAmount) {
          updatedItems[existingIndex] = { ...updatedItems[existingIndex], amount: input.amount };
        } else {
          updatedItems[existingIndex] = {
            ...updatedItems[existingIndex],
            amount: updatedItems[existingIndex].amount + input.amount,
          };
        }
        return updatedItems;
      } else {
        return [...items, input];
      }
    });
  }

  /** Remplace le contenu de la liste d'achat par les lignes fournies. */
  public restore(items: PurchaseListItem[]): void {
    this.products.set(items);
    this.onPurchaseListUpdated();
  }

  public removeFromPurchaseList(ean: string): void {
    this.products.update(items =>
      // Supprime le produit/parent par EAN + tous les enfants qui référencent ce parent
      items.filter(item => item.product.ean !== ean && item.bundleParentEan !== ean)
    );
    this.onPurchaseListUpdated();
  }

  onPurchaseListUpdated(): void {
    this.totalItemCount.set(this.getTotalCount());
    this.totalListPrice.set(this.getTotalPrice());
  }

  private getTotalPrice(): number {
    let total = 0;
    this.displayItems().forEach(p => (total += p.amount * p.product.price));
    return total;
  }

  private getTotalCount(): number {
    let total = 0;
    this.displayItems().forEach(p => (total += p.amount));
    return total;
  }

  reset(): void {
    this.isSentToComPlus = false;
    this.opportunityNumber = null;
    this.products.set([]);
    this.totalItemCount.set(0);
    this.totalListPrice.set(0);
    this.customActionButton.set(null);
    this.setAddAnotherProductLabel();
    this.onPurchaseListUpdated();
    this.discountPercentage.set(null);
  }

  setAddAnotherProductLabel(label: string = undefined): void {
    if (!label) {
      label = this.DEFAULT_ADD_ANOTHER_PRODUCT_LABEL;
    }

    this.addAnotherProductLabel.set(label);
  }

  setDiscountPercentage(discountPercentage: number): void {
    this.discountPercentage.set(discountPercentage);
  }
}
